import { ReactElement, useEffect, useMemo } from 'react';
import { GetServerSideProps, NextApiRequest } from 'next';

import { CART_ITEMS_URL } from '~/constants/cart';
import { LAYOUT_TYPES } from '~/constants/global';
import { fetchJson } from '~/lib/fetch';
import { getCookieString } from '~/lib/util';
import { getCheckoutDataGA4, gtag } from '~/lib/util/dataLayer';
import BaseLayout from '~/layouts/BaseLayout';
import { getFontFamilyClass } from '~/layouts/util';
import { CartContext } from '~/contexts/cart';
import { useCart } from '~/lib/cart';
import useUser from '~/lib/user';
import { CartProps, SaveForLaterItems } from '~/types/cart';
import { UserObjectProps } from '~/types/user';
import { useBilling } from '~/hooks/checkout';
import { USER_SESSION_ENDPOINT_URL } from '~/lib/login/constants';

import { Container, CheckoutBar } from '~/components/cart';
import { useSavedCart } from '~/lib/cart/useCart';

interface QueryProps {
  initialCart: CartProps;
  initialUser: UserObjectProps;
  initialSavedItems: SaveForLaterItems;
}

export default function Cart({
  initialCart,
  initialUser,
  initialSavedItems,
}: Readonly<QueryProps>): ReactElement {
  const { cart } = useCart(initialCart);
  const { user, isLoading: isUserLoading } = useUser(initialUser);
  const { items: savedItems } = useSavedCart(initialSavedItems);
  const { billingDetails } = useBilling(user, isUserLoading);

  useEffect(() => {
    /**
     * Timeout delays the call and will add the event after the ga4 hook on the layout.
     * This is because effects are called from children to parent and we need to set this
     * after the one on the layout.
     *
     * More info: https://github.com/appsumo/appsumo-next/pull/214#discussion_r1195415137
     **/
    setTimeout(() => {
      if (cart?.subtotal > 0) {
        const eventData = getCheckoutDataGA4(cart);
        gtag('event', 'add_to_cart', eventData);
      }
    });
    // we want this to only fire once on mount, and we should re-fire any time cart contents change
    // no need to watch entire cart
  }, [cart?.subtotal]); // eslint-disable-line react-hooks/exhaustive-deps

  const providerValue = useMemo(
    () => ({
      cart,
      user,
      billingDetails,
      savedItems,
    }),
    [cart, user, billingDetails, savedItems],
  );

  return (
    <div className="relative">
      <CartContext.Provider value={providerValue}>
        <div className="mx-auto max-w-screen-xl p-4 lg:pt-6 lg:pb-[88px]">
          <Container />
        </div>
        <CheckoutBar />
      </CartContext.Provider>
    </div>
  );
}

Cart.getLayout = function getLayout(page: React.ReactNode): ReactElement {
  // Layout pacing was changed to allow for the klarna header to be above the checkout page
  return (
    <BaseLayout
      className={`${getFontFamilyClass()} flex min-h-screen flex-col font-sans`}
      pageType={LAYOUT_TYPES.CART}
    >
      {page}
    </BaseLayout>
  );
};

export const getServerSideProps: GetServerSideProps = async ({
  req,
}): Promise<{ props: QueryProps }> => {
  const cookieString = getCookieString(req as NextApiRequest);
  const headers = {
    cookie: cookieString,
  };
  let cart, user, savedItems; // Keep undefined in case of error

  try {
    const fetchOptions = {
      method: 'GET',
      include: 'credentials',
      headers,
    };

    // Added a query param to make sure the auto campaign coupon is applied
    // This may not be automatically added if the user goes directly to the cart page
    cart = await fetchJson(
      `${CART_ITEMS_URL}?apply_auto_campaign_coupon=true`,
      fetchOptions,
    );
    savedItems = await fetchJson('/api/wishlists/saved/items/', fetchOptions);
    user = await fetchJson(USER_SESSION_ENDPOINT_URL, fetchOptions);
  } catch (error: any) {
    // This shouldn't happen, should show in sentry
    console.error(error);
  }

  return {
    props: {
      initialCart: cart,
      initialUser: user,
      initialSavedItems: savedItems,
    },
  };
};
